import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ReactPlayer from 'react-player';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { IconButton, CircularProgress } from '@mui/material';
import VideoControls from './VideoControls';
import { getClipWindow, clampToClip, clipLength } from './clipWindow';
import { toSecond, getUser } from '../../common/utilities';
import { getClipEditMode } from '../../common/permissions';
import gameService from '../../services/game.service';
import EditIcon from '@mui/icons-material/Edit';
import PlayerTagEditRequestDialog from './PlayerTagEditRequestDialog'
import PlayerSelector from './PlayerSelector';
import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';

const styles = {
    action: {
        color: '#ffffff',
        fontSize: 14,
        display: 'flex',
        width: '100%',
        justifyContent: 'center'
    },
    actionnormal: {
        color: '#ffffff',
        fontSize: 14,
        display: 'flex',
        width: '50%'
    },
    buttonBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 20px 20px 20px',
        width: '100%'
    },
    actionControls: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        margin: 'auto',
        direction: 'ltr',
        padding: '10px 0',
        paddingBottom: 'env(safe-area-inset-bottom, 10px)'
    },
    correctionControl: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'space-between',
        margin: 'auto'
    },
    correctionAction: {
        border: '2px solid red',
        display: 'flex',
        justifyContent: 'center',
        alignItem: 'center',
        fontSize: 16,
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        position: 'fixed',
        top: 0,
        left: 0
    },
    button: {
        color: '#ffffff',
        backgroundColor: '#80808069'
    },
    videoWrapper: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100dvh', // Use dynamic viewport height for better mobile support
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end', // Align video from bottom so controls stay visible
        backgroundColor: '#000',
        zIndex: 9998,
        overflow: 'hidden',
        paddingBottom: 70 // Space for controls
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10
    }
};
export default function TagVideo({ tagList, muteState, setOpen, gameId, cur_player_id }) {
    const player = useRef(null);
    const [play, setPlay] = useState(false); // Start paused until seeked
    const [ready, setReady] = useState(false);
    const [curIdx, setCurIdx] = useState(0);
    const [url, setUrl] = useState(tagList[0].url);
    const [videoURL, setVideoURL] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [playerList, setPlayerList] = useState([]);
    const [currPlayerId, setCurrPlayerId] = useState('');
    const [tagEditOpen, setTagEditOpen] = useState(false);
    const clipEditMode = getClipEditMode(getUser());
    const [playRate, setPlayRate] = useState(1.0);
    const [currentTime, setCurrentTime] = useState(0);
    const [muted, setMuted] = useState(muteState ?? false);
    const handle = useFullScreenHandle();
    const [isLandscape, setIsLandscape] = useState(window.innerHeight < window.innerWidth);
    const [initialLoading, setInitialLoading] = useState(true);
    const isFirstClip = curIdx === 0;
    const isLastClip = curIdx === tagList.length - 1;
    const { t } = useTranslation();
    // const handleOpen = () => setModalOpen(true);
    const handleClose = () => {
        setModalOpen(false);
        setPlay(true);
    };

    useEffect(() => {
        if (url?.includes('youtube.com') || url?.includes('youtu.be')) {
            gameService.getNewStreamURL(url).then((res) => {
                setVideoURL(res.result.video_url);
            });
        } else setVideoURL(url);
    }, [url]);


    useEffect(() => {
        if (!ready) return;
        if (!tagList.length) return;
        playTagByIdx(0);
    }, [tagList, ready]);

    useEffect(() => {
        const handleResize = () => {
            setIsLandscape(window.innerHeight < window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    // Auto-rotate to landscape mode on mobile
    useEffect(() => {
        const lockLandscape = async () => {
            try {
                // Check if Screen Orientation API is available
                if (window.screen?.orientation?.lock) {
                    await window.screen.orientation.lock('landscape');
                }
            } catch (err) {
                // Orientation lock not supported or denied - that's ok
            }
        };

        lockLandscape();

        // Unlock orientation when component unmounts
        return () => {
            try {
                if (window.screen?.orientation?.unlock) {
                    window.screen.orientation.unlock();
                }
            } catch (err) {
                // Ignore errors on unlock
            }
        };
    }, []);

    const seekTo = (sec) => player.current.seekTo(sec);

    // onProgress only ticks a few times a second, so ask the player directly
    // rather than seeking relative to a stale currentTime.
    const nowSeconds = () => player.current?.getCurrentTime() ?? currentTime;

    const clipWindow = getClipWindow(tagList[curIdx]);

    // The controls sit in a portal to escape the Dialog DOM, but a portal into
    // document.body is outside the fullscreen element and therefore invisible
    // while fullscreen is open -- so they follow the video in and out of it.
    const controlsHost = (handle.active && handle.node.current) || document.body;

    // Every manual seek stays inside the current clip. Outside it, onProgress
    // below either snaps back to the start or hands over to the next clip, which
    // would make the seek buttons and the scrubber look like they do nothing.
    const seekWithinClip = (seconds) => {
        const target = clampToClip(seconds, clipWindow);

        seekTo(target);
        setCurrentTime(target);
    };

    const playTagByIdx = (i) => {
        const start_time = tagList[i]?.start_time ?? tagList[i]?.player_tag_start_time;
        const tagUrl = tagList[i]?.url;
        const isSameVideo = tagUrl === url;

        setPlay(false);
        setCurIdx(i);

        const seconds = toSecond(start_time);

        setCurrentTime(seconds);

        if (isSameVideo) {
            // Same video source - seek immediately
            seekTo(seconds);
        } else {
            // Different video source - update URL and wait for it to load
            setUrl(tagUrl);
            setTimeout(() => seekTo(seconds), 200);
        }
    };

    const handleSeek = () => {
        setInitialLoading(false);
        setPlay(true);
    };

    const onProgress = (seconds) => {
        setCurrentTime(seconds);

        const { start: startTime, end: endTime } = clipWindow;

        if (seconds <= startTime) {
            seekTo(startTime);
            setPlay(true);
        } else if (seconds >= endTime) {
            if (curIdx < tagList.length - 1) PlayVideo(1);
            else {
                setPlay(true);
                setOpen(false);
            }
        }
    };

    const PlayVideo = (add) => {
        let index = curIdx + add;
        playTagByIdx(index);
    };

    // const clipCorrections = async (tags) => {
    //     let teamId;

    //     if (tags) {
    //         tags.map((data) => {
    //             teamId = data.team_id;
    //             setCurrPlayerId(data.player_id);
    //         });
    //     }

    //     await gameService.getGameTeamPlayersByTeam(teamId, gameId).then((res) => {
    //         setPlayerList(res);
    //         setPlay(false);
    //         handleOpen();
    //     });
    // };

    return (
        <>
            <PlayerSelector
                playerList={playerList}
                currPlayerId={currPlayerId}
                tagList={tagList}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                handleClose={handleClose}
                setPlay={setPlay}
                curIdx={curIdx}
                style={{ position: 'fixed', zIndex: 10000 }}
            />

            <FullScreen handle={handle}>
                <div className="player-shared-wrapper tag-video" style={styles.videoWrapper}>
                    {initialLoading && (
                        <div style={styles.loadingOverlay}>
                            <CircularProgress style={{ color: '#ffffff' }} />
                        </div>
                    )}
                    <ReactPlayer
                        key={`player-${isLandscape ? 'landscape' : 'portrait'}`} // Force re-render on orientation change
                        className="react-shared-player"
                        url={videoURL}
                        muted={muted}
                        ref={player}
                        onPlay={() => setPlay(true)}
                        onPause={() => setPlay(false)}
                        onReady={() => setReady(true)}
                        onSeek={handleSeek}
                        onProgress={(p) => onProgress(p.playedSeconds)}
                        progressInterval={250}
                        playbackRate={playRate}
                        playing={play}
                        controls={false}
                        playsinline={true}
                        width="100%"
                        height="100%"
                        style={{ opacity: initialLoading ? 0 : 1 }}
                    />
                </div>

                {clipEditMode !== null && (
                    <div
                        style={{
                            position: 'fixed',
                            left: 8,
                            top: 8,
                            zIndex: 9999,
                            color: 'red'
                        }}
                        onClick={() => {
                            setPlay(false);
                            setTagEditOpen(true);
                        }}
                    >
                        <EditIcon style={{ color: 'red', padding: 3, borderRadius: 60, textAlign: 'center', cursor: 'pointer' }} />
                    </div>
                )}

                <IconButton
                    aria-label="close"
                    onClick={(e) => setOpen(false)}
                    sx={{
                        position: 'fixed',
                        right: 8,
                        top: 8,
                        zIndex: 9999,
                        color: 'red'
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {/* Player name and action labels - top left corner, one row after edit button */}
                {!!tagList[curIdx] && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 8,
                            left: 40,
                            zIndex: 9999,
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 8
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: 'rgb(254 124 1 / 69%)',
                                padding: '5px 10px',
                                borderRadius: 5,
                                color: '#fff',
                                fontSize: 12
                            }}
                        >
                            #{tagList[curIdx]?.jersey} {tagList[curIdx]?.player_name ?? tagList[curIdx]?.player_names}
                        </div>
                        <div
                            style={{
                                backgroundColor: 'rgb(62 62 62 / 62%)',
                                padding: '5px 10px',
                                borderRadius: 5,
                                color: '#fff',
                                fontSize: 12
                            }}
                        >
                            {tagList[curIdx]?.action_name ?? tagList[curIdx]?.action_names}, {tagList[curIdx]?.action_type}, {tagList[curIdx]?.action_result}
                        </div>
                    </div>
                )}
            </FullScreen>

            {/* Controls render through a portal; see controlsHost above for where and why. */}
            {ReactDOM.createPortal(
                <div style={styles.actionControls} className="play-action-controls">
                    <div style={styles.buttonBox}>
                        <VideoControls
                            playing={play}
                            muted={muted}
                            fullscreen={handle.active}
                            position={Math.max(0, currentTime - clipWindow.start)}
                            duration={clipLength(clipWindow)}
                            onTogglePlay={() => setPlay((p) => !p)}
                            onSeekBy={(delta) => seekWithinClip(nowSeconds() + delta)}
                            onScrub={(seconds) => seekWithinClip(clipWindow.start + seconds)}
                            onSlow={() => setPlayRate(0.5)}
                            onFast={() => setPlayRate((s) => s + 0.5)}
                            onToggleMute={() => setMuted((m) => !m)}
                            onToggleFullscreen={() => (handle.active ? handle.exit() : handle.enter())}
                            showSkip
                            disablePrev={isFirstClip}
                            disableNext={isLastClip}
                            onPrev={() => PlayVideo(-1)}
                            onNext={() => PlayVideo(1)}
                        />
                    </div>
                </div>,
                controlsHost
            )}
            {tagList.length > 0 && (
                <PlayerTagEditRequestDialog
                    t={t}
                    open={tagEditOpen}
                    onClose={(flag) => {
                        setTagEditOpen(false);
                        setPlay(true);

                        // if (flag) setUpdated((c) => c + 1);
                    }}
                    player={tagList[curIdx]}
                    cur_player_id={cur_player_id}
                    applyImmediately={clipEditMode === 'immediate'}
                />
            )}
        </>
    );
}
